import { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@services/api';
import OAuthButtons from '@components/OAuthButtons';
import { toDisplayError } from '../utils/apiError';
import { registrationTimezone } from '../utils/timezones';

// Validation schema
const registerSchema = z
  .object({
    firstName: z
      .string()
      .min(1, 'auth.validation.firstNameRequired')
      .max(100, 'auth.validation.firstNameTooLong'),
    lastName: z
      .string()
      .min(1, 'auth.validation.lastNameRequired')
      .max(100, 'auth.validation.lastNameTooLong'),
    email: z.string().min(1, 'auth.validation.emailRequired').email('auth.validation.emailInvalid'),
    password: z
      .string()
      .min(8, 'auth.validation.passwordTooShort')
      .regex(/[A-Z]/, 'auth.validation.passwordUppercase')
      .regex(/[a-z]/, 'auth.validation.passwordLowercase')
      .regex(/[0-9]/, 'auth.validation.passwordNumber'),
    confirmPassword: z.string().min(1, 'auth.validation.confirmPasswordRequired'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'auth.validation.passwordsMustMatch',
    path: ['confirmPassword'],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: RegisterFormData) => {
    setServerError(null);
    setIsSubmitting(true);

    try {
      await apiClient.post('/v1/auth/register', {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        // REV-684: the profile's default timezone.
        timezone: registrationTimezone(),
      });

      // Redirect to login with the success message, forwarding any return
      // path so a flow that sent the user here to sign up (e.g. an invitation
      // link) resumes after login instead of dumping them on the dashboard
      // (REV-521).
      void navigate('/login', {
        state: {
          message: t('auth.register.success'),
          from: (location.state as { from?: unknown } | null)?.from,
        },
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      setServerError(toDisplayError(err, t('auth.errors.registerFailed')));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
          {t('auth.register.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('auth.register.subtitle')}
        </Typography>
      </Box>

      {/* Server error alert */}
      {serverError && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {serverError}
        </Alert>
      )}

      {/* Register form */}
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              {...register('firstName')}
              label={t('auth.fields.firstName')}
              autoComplete="given-name"
              error={!!errors.firstName}
              helperText={errors.firstName ? t(errors.firstName.message as string) : undefined}
              disabled={isSubmitting}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              {...register('lastName')}
              label={t('auth.fields.lastName')}
              autoComplete="family-name"
              error={!!errors.lastName}
              helperText={errors.lastName ? t(errors.lastName.message as string) : undefined}
              disabled={isSubmitting}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
        </Grid>

        <TextField
          {...register('email')}
          label={t('auth.fields.email')}
          type="email"
          autoComplete="email"
          error={!!errors.email}
          helperText={errors.email ? t(errors.email.message as string) : undefined}
          disabled={isSubmitting}
          sx={{ mt: 2 }}
          slotProps={{ inputLabel: { shrink: true } }}
        />

        <TextField
          {...register('password')}
          label={t('auth.fields.password')}
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          error={!!errors.password}
          helperText={errors.password ? t(errors.password.message as string) : undefined}
          disabled={isSubmitting}
          sx={{ mt: 2 }}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <TextField
          {...register('confirmPassword')}
          label={t('auth.fields.confirmPassword')}
          type={showConfirmPassword ? 'text' : 'password'}
          autoComplete="new-password"
          error={!!errors.confirmPassword}
          helperText={
            errors.confirmPassword ? t(errors.confirmPassword.message as string) : undefined
          }
          disabled={isSubmitting}
          sx={{ mt: 2, mb: 3 }}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showConfirmPassword ? t('auth.hidePassword') : t('auth.showPassword')
                    }
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        {/* Password requirements hint */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
          {t('auth.register.passwordHint')}
        </Typography>

        {/* Submit button */}
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={isSubmitting}
          sx={{ mb: 2 }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t('auth.register.submit')
          )}
        </Button>
      </Box>

      {/* OAuth buttons */}
      {/* REV-677: no Divider here. OAuthButtons returns null when no providers
          are configured, which is the default — so a divider owned by this page
          left TWO adjacent rules with nothing between them, reading as a gap
          where something failed to load. The divider that belongs to the OAuth
          block now lives inside it, and appears only when it renders. */}
      <OAuthButtons mode="register" />

      {/* This one is unconditional: it separates the form from the link below
          it, which is there whether or not OAuth is configured. */}
      <Divider sx={{ my: 3 }} />

      {/* Login link */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('auth.register.hasAccount')}{' '}
          <Link component={RouterLink} to="/login">
            {t('auth.register.loginLink')}
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}

export default RegisterPage;
